import { ArrowLeft } from "lucide-react";
import { useEffect, type ReactNode } from "react";
import { Link } from "react-router";

const PRODUCT = "Supperkeep";

/** Route document title: "{Page} — Supperkeep". */
export function useDocumentTitle(title: string | null | undefined) {
  useEffect(() => {
    document.title = title ? `${title} — ${PRODUCT}` : PRODUCT;
  }, [title]);
}

export function PageHeader({ title, eyebrow, description, actions, back, children }: { title: ReactNode; eyebrow?: ReactNode; description?: ReactNode; actions?: ReactNode; back?: { to: string; label: string }; children?: ReactNode }) {
  return (
    <header className="page-header">
      {back ? (
        <Link to={back.to} className="page-header__back">
          <ArrowLeft size={18} aria-hidden="true" />
          {back.label}
        </Link>
      ) : null}
      <div className="page-header__row">
        <div className="page-header__titles">
          {eyebrow ? <p className="page-header__eyebrow">{eyebrow}</p> : null}
          <h1 className="page-header__title" tabIndex={-1} data-page-title>
            {title}
          </h1>
          {description ? <p className="page-header__description">{description}</p> : null}
        </div>
        {actions ? <div className="page-header__actions">{actions}</div> : null}
      </div>
      {children}
    </header>
  );
}

export function Panel({ title, description, actions, children, className, headingLevel = 2, id }: { title?: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; headingLevel?: 2 | 3; id?: string }) {
  const Heading = headingLevel === 2 ? "h2" : "h3";
  return (
    <section className={["panel", className ?? ""].join(" ")} aria-labelledby={title && id ? `${id}-title` : undefined} id={id}>
      {title || actions ? (
        <div className="panel__header">
          <div>
            {title ? (
              <Heading className="panel__title" id={id ? `${id}-title` : undefined}>
                {title}
              </Heading>
            ) : null}
            {description ? <p className="panel__description">{description}</p> : null}
          </div>
          {actions ? <div className="panel__actions">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}
